<!-- eslint-disable vue/multi-word-component-names -->
<template>
  <div class="page-container feedback-page" v-loading="loading">
    <div class="page-header">
      <div>
        <h2>问题反馈</h2>
        <p>用于收集真实使用过程中的问题与改进建议，系统会自动记录反馈人与时间，便于管理员跟进与回复。</p>
      </div>
      <div class="meta">
        <div class="meta-row">
          <span class="meta-label">反馈人</span>
          <span class="meta-value">{{ currentUserLabel }}</span>
        </div>
        <div class="meta-row">
          <span class="meta-label">当前时间</span>
          <span class="meta-value">{{ nowText }}</span>
        </div>
      </div>
    </div>

    <!-- 提交区域 -->
    <el-card class="feedback-card" shadow="hover">
      <template #header>
        <div class="card-header">
          <span>新建反馈</span>
        </div>
      </template>

      <el-form label-width="80px" class="feedback-form">
        <el-form-item label="问题描述">
          <el-input
            v-model="form.description"
            type="textarea"
            :rows="4"
            placeholder="请尽量说明：在哪个页面、做了什么操作、期望结果与实际结果……"
          />
        </el-form-item>

        <el-form-item label="截图">
          <div
            class="screenshot-dropzone"
            @paste.prevent="handlePaste"
          >
            <p class="hint">
              在任意界面截图后，<strong>回到这里直接 Ctrl + V 粘贴</strong> 即可自动上传；支持多张。
            </p>
            <div v-if="screenshots.length" class="thumb-list">
              <div
                v-for="(shot, idx) in screenshots"
                :key="idx"
                class="thumb-item"
              >
                <img :src="shot.previewUrl" alt="截图预览" />
                <span class="thumb-name">{{ shot.fileName }}</span>
                <el-icon class="thumb-remove" @click="removeShot(idx)"><Close /></el-icon>
              </div>
            </div>
          </div>
        </el-form-item>

        <el-form-item>
          <el-button type="primary" :loading="submitting" @click="submitFeedback">
            提交反馈
          </el-button>
          <span class="small-hint">系统会自动记录反馈人和反馈时间，无需手动填写。</span>
        </el-form-item>
      </el-form>
    </el-card>

    <!-- 列表区域 -->
    <el-card class="feedback-list-card" shadow="never">
      <template #header>
        <div class="card-header">
          <span>历史反馈</span>
          <span class="card-sub" v-if="feedbackList.length">共 {{ feedbackList.length }} 条</span>
        </div>
      </template>

      <div v-if="!feedbackList.length" class="empty-tip">
        暂无反馈记录，欢迎先提一条你最关心的问题或想法。
      </div>
      <div v-else class="feedback-list">
        <div
          v-for="item in feedbackList"
          :key="item.id"
          class="feedback-item"
        >
          <div class="item-main">
            <div class="item-header">
              <span class="item-author">{{ item.user_name || item.username || '未知用户' }}</span>
              <span class="item-time">{{ formatTime(item.created_at || item.createdAt || item.client_time) }}</span>
            </div>
            <div class="item-desc">
              {{ item.description }}
            </div>
            <div v-if="item.screenshots && item.screenshots.length" class="item-images">
              <el-image
                v-for="(img, index) in item.screenshots"
                :key="index"
                :src="img"
                :preview-src-list="item.screenshots"
                fit="contain"
              />
            </div>
          </div>

          <div class="item-replies" v-if="item.replies && item.replies.length">
            <div
              v-for="(reply, rIndex) in item.replies"
              :key="rIndex"
              class="reply-row"
            >
              <span class="reply-label">管理员回复</span>
              <span class="reply-time">{{ formatTime(reply.created_at || reply.createdAt) }}</span>
              <div class="reply-content">{{ reply.content }}</div>
            </div>
          </div>

          <div
            class="reply-editor"
            v-if="isAdmin"
          >
            <el-input
              v-model="replyInput[item.id]"
              type="textarea"
              :rows="2"
              placeholder="回复此条反馈（仅管理员可见此区域）"
            />
            <div class="reply-actions">
              <el-button
                type="primary"
                size="small"
                :loading="replySubmitting[item.id]"
                @click="submitReply(item)"
              >
                回复
              </el-button>
            </div>
          </div>
        </div>
      </div>
    </el-card>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { ElMessage } from 'element-plus'
import { Close } from '@element-plus/icons-vue'
import axios from 'axios'

const loading = ref(false)
const submitting = ref(false)
const feedbackList = ref([])

const form = ref({
  description: ''
})

const screenshots = ref([]) // { fileName, previewUrl, dataUrl }

const replyInput = ref({})
const replySubmitting = ref({})

// 当前用户与角色，从 localStorage 与 /api/auth/me/ 中获取
const currentUser = ref(null)

const currentUserLabel = computed(() => {
  const u = currentUser.value
  if (!u) return '未登录'
  if (u.realName) return `${u.realName} (${u.username || ''})`
  return u.username || '未登录'
})

const isAdmin = computed(() => {
  const u = currentUser.value
  return u && (u.role === 'admin' || u.is_superuser)
})

const nowText = computed(() => {
  const d = new Date()
  const pad = (n) => (n < 10 ? '0' + n : '' + n)
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`
})

const formatTime = (val) => {
  if (!val) return ''
  try {
    const d = typeof val === 'string' ? new Date(val) : val
    if (Number.isNaN(d.getTime())) return val
    const pad = (n) => (n < 10 ? '0' + n : '' + n)
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`
  } catch {
    return val
  }
}

const handlePaste = (e) => {
  const items = e.clipboardData?.items || []
  let found = false
  for (const item of items) {
    if (item.kind === 'file' && item.type.startsWith('image/')) {
      const file = item.getAsFile()
      if (!file) continue
      found = true
      const reader = new FileReader()
      reader.onload = (ev) => {
        screenshots.value.push({
          fileName: file.name || '截图.png',
          previewUrl: URL.createObjectURL(file),
          dataUrl: ev.target.result
        })
      }
      reader.readAsDataURL(file)
    }
  }
  if (!found) {
    ElMessage.info('剪贴板中未检测到图片，可先截图再 Ctrl+V 粘贴。')
  }
}

const removeShot = (index) => {
  screenshots.value.splice(index, 1)
}

const loadCurrentUser = async () => {
  const raw = localStorage.getItem('currentUser')
  if (raw) {
    try {
      currentUser.value = JSON.parse(raw)
    } catch {
      currentUser.value = null
    }
  }
  try {
    const res = await axios.get('/api/auth/me/')
    if (res.data.status === 'ok' && res.data.data) {
      currentUser.value = res.data.data
      localStorage.setItem('currentUser', JSON.stringify(res.data.data))
    }
  } catch {
    // ignore
  }
}

const loadFeedback = async () => {
  loading.value = true
  try {
    const res = await axios.get('/api/feedback/')
    if (res.data && res.data.status === 'ok') {
      const list = res.data.data || []
      feedbackList.value = list.map((item) => ({
        ...item,
        screenshots: item.screenshots || item.images || []
      }))
    } else {
      feedbackList.value = []
    }
  } catch (e) {
    ElMessage.error(e.response?.data?.message || '加载反馈列表失败')
  } finally {
    loading.value = false
  }
}

const submitFeedback = async () => {
  if (!form.value.description && !screenshots.value.length) {
    ElMessage.warning('请至少填写问题描述或提供一张截图')
    return
  }
  submitting.value = true
  try {
    const payload = {
      description: form.value.description,
      screenshots: screenshots.value.map((s) => s.dataUrl),
      client_time: new Date().toISOString()
    }
    const res = await axios.post('/api/feedback/', payload)
    if (res.data && res.data.status === 'ok') {
      ElMessage.success('反馈已提交')
      form.value.description = ''
      screenshots.value = []
      await loadFeedback()
    } else {
      ElMessage.error(res.data?.message || '提交失败')
    }
  } catch (e) {
    ElMessage.error(e.response?.data?.message || '提交失败')
  } finally {
    submitting.value = false
  }
}

const submitReply = async (item) => {
  const content = replyInput.value[item.id]
  if (!content) {
    ElMessage.warning('回复内容不能为空')
    return
  }
  replySubmitting.value[item.id] = true
  try {
    const res = await axios.post(`/api/feedback/${item.id}/reply/`, {
      content,
      client_time: new Date().toISOString()
    })
    if (res.data && res.data.status === 'ok') {
      ElMessage.success('回复已提交')
      replyInput.value[item.id] = ''
      await loadFeedback()
    } else {
      ElMessage.error(res.data?.message || '回复失败')
    }
  } catch (e) {
    ElMessage.error(e.response?.data?.message || '回复失败')
  } finally {
    replySubmitting.value[item.id] = false
  }
}

onMounted(async () => {
  await loadCurrentUser()
  await loadFeedback()
})
</script>

<style scoped>
.feedback-page {
  min-height: 100%;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 20px;
  gap: 16px;
}

.page-header h2 {
  margin: 0 0 6px 0;
  font-size: 24px;
  font-weight: 700;
  color: #0f172a;
}

.page-header p {
  margin: 0;
  font-size: 14px;
  color: #64748b;
  max-width: 560px;
}

.meta {
  flex-shrink: 0;
  min-width: 220px;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
  padding: 8px 12px;
  background: #f8fafc;
}

.meta-row {
  display: flex;
  justify-content: space-between;
  font-size: 13px;
  color: #475569;
}

.meta-row + .meta-row {
  margin-top: 4px;
}

.meta-label {
  color: #64748b;
}

.meta-value {
  font-weight: 500;
}

.feedback-card {
  margin-bottom: 20px;
}

.card-header {
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 600;
}

.card-sub {
  font-size: 12px;
  color: #94a3b8;
}

.feedback-form {
  max-width: 900px;
}

.screenshot-dropzone {
  border-radius: 8px;
  border: 1px dashed #cbd5f5;
  padding: 12px;
  background: #f8fafc;
  width: 100%;
}

.hint {
  margin: 0 0 8px 0;
  font-size: 13px;
  color: #64748b;
}

.small-hint {
  margin-left: 12px;
  font-size: 12px;
  color: #9ca3af;
}

.thumb-list {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
}

.thumb-item {
  width: 140px;
  border-radius: 8px;
  background: #fff;
  border: 1px solid #e2e8f0;
  padding: 6px;
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
}

.thumb-item img {
  width: 100%;
  height: 80px;
  object-fit: cover;
  border-radius: 4px;
}

.thumb-name {
  font-size: 12px;
  color: #64748b;
  text-align: center;
}

.thumb-remove {
  position: absolute;
  top: 4px;
  right: 4px;
  cursor: pointer;
  font-size: 14px;
  color: #9ca3af;
}

.feedback-list-card {
  margin-top: 8px;
}

.empty-tip {
  padding: 16px;
  font-size: 13px;
  color: #94a3b8;
}

.feedback-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.feedback-item {
  padding: 12px 0;
  border-bottom: 1px solid #e5e7eb;
}

.feedback-item:last-child {
  border-bottom: none;
}

.item-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 13px;
  color: #64748b;
  margin-bottom: 4px;
}

.item-author {
  font-weight: 600;
  color: #0f172a;
}

.item-time {
  font-size: 12px;
  color: #9ca3af;
}

.item-desc {
  font-size: 14px;
  color: #111827;
  white-space: pre-wrap;
  margin-bottom: 6px;
}

.item-images {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.item-images :deep(.el-image) {
  width: 120px;
  height: 80px;
  border-radius: 6px;
  border: 1px solid #e5e7eb;
  overflow: hidden;
}

.item-replies {
  margin-top: 8px;
  padding-left: 8px;
  border-left: 2px solid #bfdbfe;
}

.reply-row {
  margin-bottom: 4px;
}

.reply-label {
  font-size: 12px;
  font-weight: 600;
  color: #2563eb;
  margin-right: 8px;
}

.reply-time {
  font-size: 12px;
  color: #9ca3af;
}

.reply-content {
  font-size: 13px;
  color: #111827;
  margin-top: 2px;
  white-space: pre-wrap;
}

.reply-editor {
  margin-top: 8px;
}

.reply-actions {
  margin-top: 4px;
  text-align: right;
}
</style>

